import React, { useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { selectCustomEmailFilter, selectEmailFilterSender, selectShowEmailFilterModal, setEmailFilterSender } from '../../store/emailHub/emailFilters'
import { useFetchEmailThreads } from '../../views/EmailV2/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetExternalUsersQuery } from '../../store/externalUser/externalUser'
import { SelectOption } from '../SelectDropdown/SelectDropdown'
import { AutocompleteSelectDropdown } from './AutocompleteSelectDropdown/AutocompleteSelectDropdown'

export interface EmailDateFilterProps {
  disabled?: boolean
}

export const EmailContactFilter: React.FC<EmailDateFilterProps> = ({ disabled = false }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const showEmailFilterModal = useAppSelector(selectShowEmailFilterModal)
  const selectedSender = useAppSelector(selectEmailFilterSender)
  const customFilter = useAppSelector(selectCustomEmailFilter)

  const { fetchEmailThreads } = useFetchEmailThreads()

  const [query, setQuery] = useState<string>('')

  const isSearching = query.trim().length > 0

  const { data: defaultContactsResponse, isFetching: frequentContactsLoading } = useGetExternalUsersQuery(
    !isSearching ? { accountId, pageRequest: { page: 0, size: 10 } } : skipToken,
  )

  const { data: searchContactsResponse, isFetching: searchContactsLoading } = useGetExternalUsersQuery(
    isSearching ? { accountId, name: query, pageRequest: { page: 0, size: 10 } } : skipToken,
  )

  const { data: selectedContactResponse, isFetching: selectedContactLoading } = useGetExternalUsersQuery(
    selectedSender ? { accountId, emails: [selectedSender], pageRequest: { page: 0, size: 1 } } : skipToken,
    { refetchOnMountOrArgChange: true },
  )

  const defaultContactOptions = useMemo(() => {
    const contacts = defaultContactsResponse?.data ?? []
    return contacts
      .filter((c) => !!c.email)
      .map((c) => ({ id: c.email!, label: getUserDisplayName(c) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [defaultContactsResponse])

  const searchContactOptions = useMemo(() => {
    const contacts = searchContactsResponse?.data ?? []
    return contacts
      .filter((c) => !!c.email)
      .map((c) => {
        const label = getUserDisplayName(c)
        return {
          id: c.email!,
          label,
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [searchContactsResponse])

  const contactOptions: SelectOption[] = useMemo(() => {
    if (isSearching) {
      return searchContactOptions
    }
    return defaultContactOptions
  }, [defaultContactOptions, isSearching, searchContactOptions])

  const selectedContact: SelectOption | null = useMemo(() => {
    if (!selectedSender || selectedContactLoading) {
      return null
    }

    const selectedContactData = selectedContactResponse?.data?.find((c) => c.email?.toLowerCase() === selectedSender.toLowerCase())
    if (selectedContactData?.email) {
      return { id: selectedContactData.email, label: getUserDisplayName(selectedContactData) }
    }

    return { id: selectedSender, label: selectedSender }
  }, [selectedSender, selectedContactResponse, selectedContactLoading])

  const handleContactSelected = async (option: SelectOption) => {
    dispatch(setEmailFilterSender(option.id))
    if (!showEmailFilterModal) {
      await fetchEmailThreads({ filter: { ...customFilter, senders: [option.id] } })
    }
  }

  const handleDeselect = async () => {
    if (!selectedSender) {
      return
    }
    dispatch(setEmailFilterSender(null))
    if (!showEmailFilterModal) {
      await fetchEmailThreads({ filter: { ...customFilter, senders: [] } })
    }
  }

  return (
    <div className='d-flex flex-column gap-1'>
      <div className='lead-extra-small'>Filter by contact {selectedContact ? '(1)' : null}</div>
      <AutocompleteSelectDropdown
        initialSelectedOption={selectedContact}
        options={contactOptions}
        disabled={disabled}
        optionsLoading={frequentContactsLoading || searchContactsLoading}
        selectedLoading={selectedContactLoading}
        placeholder='Select a contact'
        onOptionSelected={handleContactSelected}
        onQueryChange={setQuery}
        headerText={isSearching ? undefined : 'Recently added'}
        onDeselect={handleDeselect}
      />
    </div>
  )
}
